import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import { Button, Loader } from '@platform/ui-library';
import {
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Label,
  VirtualScroll,
  VirtualScrollApi
} from '@app/form_components';
import {
  useDesign,
  useLatestDesign,
  useServices
} from '@app/hooks';
import { VersionSelectorControl } from '@app/page-components';
import {
  AvsChangeLogs,
  Design,
  DesignChangeMap
} from '@app/types';
import { formatDateTime } from '@app/utilities';
import { Utility } from '@app/utilities/Utility';
import { ChangeSetWrapper } from './change-set-wrapper';
import style from './change-log.module.scss';

const PANEL_ESTIMATE = 600;

type ChangeLogProps = {
  serialized?: Design;
  version?: string;
};

export function ChangeLog({ serialized, version: versionProp }: ChangeLogProps) {
  const { avsService } = useServices();
  const { branchId } = useDesign();
  const latestDesign = useLatestDesign();
  const latestVersion = latestDesign?.version ?? versionProp ?? serialized?.version;
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [changeLogs, setNewChangeLogs] = useState<AvsChangeLogs[] | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const apiRef = useRef<VirtualScrollApi | null>(null);
  const changelogVersion = selectedVersion ?? latestVersion;

  useEffect(() => {
    (async () => {
      if (!changelogVersion) {
        return;
      }
      setNewChangeLogs([]);
      setNewChangeLogs(await avsService.getChangeLogs(branchId, changelogVersion));
    })();
  }, [changelogVersion]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [changeLogs]);

  const navigateTo = (index: number) => {
    const scrollWorked = apiRef.current?.scrollToIndex(index, {
      align: 'start',
      behavior: 'smooth'
    });
    if (scrollWorked) {
      setCurrentIndex(index);
    }
  };

  return (
    <>
      <div className={style.changeLogControls}>
        <div className={style.versionSelector}>
          <Label tooltip='Will display Change Logs for the selected version and up to 20 previous ones'>Selected Version</Label>
          <fieldset disabled={!changeLogs}>
            <VersionSelectorControl
              branchId={branchId}
              eagerLoad
              name="changelogVersion"
              onChange={(newChangelogVersion) => setSelectedVersion(newChangelogVersion ?? null)}
              value={changelogVersion ?? ''}
            />
          </fieldset>
        </div>
        <div className={style.navigator}>
          <Button
            icon={ChevronUpIcon}
            invert
            onClick={() => navigateTo(Math.max(0, currentIndex - 1))}
            theme={['square', 'outline']}
            tooltip='Previous version'
          />
          <Button
            icon={ChevronDownIcon}
            invert
            onClick={() => navigateTo(Math.min(currentIndex + 1, (changeLogs?.length ?? 0) - 1))}
            theme={['square', 'outline']}
            tooltip='Next version'
          />
        </div>
      </div>

      {(!changeLogs) ? (
        <Loader loading />
      ) : (
        <VirtualScroll
          apiRef={apiRef}
          classNames={{ scrollContainer: style.virtualScroll }}
          estimateItemSize={PANEL_ESTIMATE}
          footer={null}
          getItemKey={(changeMap) => changeMap.version}
          header={null}
          items={changeLogs}
          onScrollIndexChange={setCurrentIndex}
          renderItem={(changeMap) => renderChangeLogPanel(changeMap, serialized)}
        />
      )}
    </>
  );
}

function renderChangeLogPanel(changeMap: AvsChangeLogs, serialized: Design | undefined) {
  const { author, changeLogMap, commitMessage = '', savedFrom, version } = changeMap;
  const source = changeLogMap[0];
  const sourceKeys = Object.keys(source) as (keyof DesignChangeMap)[];

  return (
    <div className="changeLogPanel">
      <div className="changeLogPanelHeader">
        <div>
          <span className="labels">
            <i className="fe icon-file-text" />
            <span>Change Log</span>
            <i className="fe icon-hash" />
            <span>{version}</span>
            <i className="fe icon-user"></i>
            <span>{author}</span>
          </span>
          <div className="commitMessage">{commitMessage}</div>
        </div>
        <span className="created">
          {typeof savedFrom === 'string' && (
            <span>
              <span className="savedFrom">
                Saved from <strong>{savedFrom}</strong>
              </span>
            </span>
          )}
          <span className="timeStamp">{formatDateTime(changeMap.timeStamp)}</span>
        </span>
      </div>

      <div className="panel">
        {!sourceKeys.length ? (
          <h5 className="emptyMessage">No changes in this version</h5>
        ) : (
          sourceKeys.map((key, i) => {
            const filteredMap = ((key === 'settings') ? source : source[key]) ?? {};
            const filteredMapKeys = Object.keys(filteredMap);

            if (!filteredMapKeys.length) {
              return null;
            }

            return (
              <div key={i}>
                <h4>
                  {Utility.getChangeLogLabel(key)} ({filteredMapKeys.length})
                </h4>
                <ChangeSetWrapper
                  clKey={key}
                  filteredMap={filteredMap}
                  serialized={serialized}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
